import {
  loginPath,
  changePasswordPath,
  forgotPasswordPath,
  twoFactorPath,
  redirectRouteMapper,
  maintenancePath,
  loginExperimentalPath,
  changePasswordPathExperimental,
  changePasswordExperimentalPath,
  roleSelectionPath,
} from 'Consts/Strings';
import history, { historyWithForceRefresh } from 'State/history';
import { QueryVal, removeQueryWithReplace } from 'Helpers/Query';
import { hasLoggedInBefore } from 'Context/UserContext/hasLoggedInBefore';

const buildSearchParams = () => {
  const params = {};
  const url = new URL(window.location.href);

  const toParam = url.pathname + url.search + url.hash;
  if (toParam.length > 1) {
    params.to = encodeURI(toParam);
  }

  return params;
};

export const navigateTo = (path, replace = false) => {
  if (replace) {
    history.replace(path);
  } else {
    history.push(path);
  }
};

export default function handleRedirect(data) {
  const {
    attributes: { route, isSsoRedirect },
  } = data;

  const redirect = () => {
    if (route != null) {
      if (/^https?:\/\//.test(route)) {
        window.location = route;
      } else {
        navigateTo(redirectRouteMapper[route].redirect);
      }
    }
    return Promise.resolve();
  };

  // we still have some resource model / context growing pains. basically in our current user -> household context we force a redirect
  // when someone hits the reset password link if they haven't confirmed certain agreements, but we want to, as the client,
  // opt out of the redirects until we resolve the underlying architectural issue
  if (
    history.location.pathname !== forgotPasswordPath
    && history.location.pathname !== changePasswordPath
    && history.location.pathname !== loginPath
    && history.location.pathname !== twoFactorPath
    && history.location.pathname !== loginExperimentalPath
    && history.location.pathname !== changePasswordPathExperimental
  ) {
    return redirect();
  }

  // Some sort of tech debt forbids us from redirecting on authentication pages, but we need an
  // exception to this rule for SSO redirects.
  if (isSsoRedirect) {
    return redirect();
  }

  return Promise.resolve();
}

export const redirectToMaintenance = () => {
  const url = new URL(window.location.href);
  url.search = new URLSearchParams(buildSearchParams()).toString();
  url.pathname = maintenancePath;
  // Using historyWithForceRefresh so that if there's a new JS bundle, it can be loaded transparently to the user
  historyWithForceRefresh.push(url.pathname + url.search + url.hash);

  return undefined;
};

export const redirectToLogin = () => {
  if (
    window.location.pathname !== forgotPasswordPath
    && window.location.pathname !== changePasswordPath
    && window.location.pathname !== loginPath
    && window.location.pathname !== loginExperimentalPath
    && window.location.pathname !== changePasswordExperimentalPath
    && window.location.pathname !== twoFactorPath
    && window.location.pathname !== roleSelectionPath
  ) {
    const url = new URL(window.location.href);
    const params = buildSearchParams();

    const authToken = QueryVal('authToken');
    if (authToken) {
      params.authToken = authToken;
    }
    removeQueryWithReplace('authToken');
    url.search = new URLSearchParams(params).toString();
    url.pathname = loginPath;
    const newLocation = url.pathname + url.search + url.hash;
    if (hasLoggedInBefore()) {
      // This person has been logged in on this tab before, and is now being logged out.
      // This is the perfect time to refresh the page on them!
      historyWithForceRefresh.push(newLocation);
    } else {
      // This person has not been logged in before.
      // It's very possible that they *just* opened our website in a new tab, and now we're about to redirect them to the login page.
      // We don't want to use historyWithForceRefresh here because that would cause an extra, unnecessary load.
      history.push(newLocation);
    }
  }

  return undefined;
};
